// Conexão do bot com o WhatsApp: WhatsApp Web (QR Code) ou API oficial da Meta.
// Os dados da Meta vão para o backend (/meta/config), que guarda o token e o App Secret fora
// do banco que o painel lê; aqui só aparece se eles estão salvos.
// Com WHATSAPP_WEB_ATIVO=false no .env do backend, só a API oficial aparece (webDisponivel).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiAlertTriangle, FiCheck, FiCopy, FiRefreshCw, FiSend } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { auth } from '../../config/firebaseConfig';
import './ConexaoWhatsApp.css';

const apiUrl = process.env.REACT_APP_API_URL;

const STATUS_MODELO = {
  APPROVED: { texto: 'Aprovado', classe: 'qz-badge--success' },
  PENDING: { texto: 'Em análise na Meta', classe: 'qz-badge--warning' },
  IN_APPEAL: { texto: 'Em recurso', classe: 'qz-badge--warning' },
  REJECTED: { texto: 'Rejeitado', classe: 'qz-badge--danger' },
  PAUSED: { texto: 'Pausado pela Meta', classe: 'qz-badge--danger' },
  DISABLED: { texto: 'Desativado pela Meta', classe: 'qz-badge--danger' },
};

const cabecalhos = async () => ({ Authorization: `Bearer ${await auth.currentUser?.getIdToken()}` });
const erroDe = (e, padrao) => e?.response?.data?.error || padrao;

// *negrito* do WhatsApp na prévia do modelo
const comNegrito = (texto) =>
  texto.split(/(\*[^*]+\*)/).map((parte, i) =>
    /^\*[^*]+\*$/.test(parte) ? <strong key={i}>{parte.slice(1, -1)}</strong> : parte);

function Campo({ id, rotulo, ajuda, tipo = 'text', valor, onChange, placeholder }) {
  return (
    <div className="cw-campo">
      <label htmlFor={id}>{rotulo}</label>
      <input
        id={id}
        type={tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={tipo === 'password' ? 'new-password' : 'off'}
        spellCheck={false}
        aria-describedby={ajuda ? `${id}-ajuda` : undefined}
      />
      {ajuda && <span id={`${id}-ajuda`} className="cw-suave">{ajuda}</span>}
    </div>
  );
}

function Copiar({ rotulo, valor }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto e copie.');
    }
  };
  return (
    <div className="cw-copiar">
      <span className="cw-copiar__rotulo">{rotulo}</span>
      <div className="cw-copiar__linha">
        <code>{valor}</code>
        <button type="button" className="btn-secondary cw-botao" onClick={copiar} aria-label={`Copiar ${rotulo}`}>
          {copiado ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
          {copiado ? 'Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

function ConexaoWhatsApp({ onMudou }) {
  const tenantId = localStorage.getItem('tenantId');
  const [salvo, setSalvo] = useState(null);
  const [form, setForm] = useState(null);
  const [erroCarga, setErroCarga] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [modelo, setModelo] = useState({ carregando: false, dados: undefined, erro: '' });
  const [criandoModelo, setCriandoModelo] = useState(false);
  const [telefoneTeste, setTelefoneTeste] = useState('');
  const [testando, setTestando] = useState(false);

  const aplicar = (c) => {
    setSalvo(c);
    setForm({
      provedor: c.provedor,
      phoneNumberId: c.phoneNumberId,
      wabaId: c.wabaId,
      accessToken: '',
      appSecret: '',
      modeloNome: c.modeloNome,
      modeloIdioma: c.modeloIdioma,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${apiUrl}/meta/config`, { params: { sanitizedClientId: tenantId }, headers: await cabecalhos() });
        aplicar(r.data);
      } catch (e) {
        setErroCarga(erroDe(e, 'Não foi possível carregar a conexão do WhatsApp.'));
      }
    })();
  }, [tenantId]);

  const consultarModelo = useCallback(async () => {
    setModelo((m) => ({ ...m, carregando: true, erro: '' }));
    try {
      const r = await axios.get(`${apiUrl}/meta/modelo`, { params: { sanitizedClientId: tenantId }, headers: await cabecalhos() });
      setModelo({ carregando: false, dados: r.data.modelo, erro: '' });
    } catch (e) {
      setModelo({ carregando: false, dados: undefined, erro: erroDe(e, 'Não foi possível consultar o modelo na Meta.') });
    }
  }, [tenantId]);

  useEffect(() => {
    if (salvo?.provedor === 'meta' && salvo.wabaId && salvo.temToken) consultarModelo();
  }, [salvo, consultarModelo]);

  const mudar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const alterado = !!(form && salvo) && (
    form.provedor !== salvo.provedor
    || form.phoneNumberId !== salvo.phoneNumberId
    || form.wabaId !== salvo.wabaId
    || form.modeloNome !== salvo.modeloNome
    || form.modeloIdioma !== salvo.modeloIdioma
    || !!form.accessToken
    || !!form.appSecret
  );

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await axios.post(`${apiUrl}/meta/config`, { sanitizedClientId: tenantId, ...form }, { headers: await cabecalhos() });
      aplicar(r.data);
      if (r.data.provedor === 'meta') {
        if (r.data.bot?.status === 'connected') toast.success(`Conectado pela API oficial: ${r.data.numeroExibido}`);
        else toast.error(r.data.bot?.message || 'Dados salvos, mas o bot não conectou.');
      } else {
        toast.success('Conexão trocada para WhatsApp Web. Clique em Ligar e leia o QR Code.');
      }
      if (r.data.aviso) toast.warning(r.data.aviso);
      if (onMudou) onMudou(r.data.provedor);
    } catch (e) {
      toast.error(erroDe(e, 'Não foi possível salvar a conexão.'));
    } finally {
      setSalvando(false);
    }
  };

  const criarModelo = async () => {
    setCriandoModelo(true);
    try {
      const r = await axios.post(`${apiUrl}/meta/modelo`, { sanitizedClientId: tenantId }, { headers: await cabecalhos() });
      setModelo({ carregando: false, dados: r.data.modelo, erro: '' });
      toast.success('Modelo enviado para análise da Meta. Costuma sair em minutos; às vezes leva até 24h.');
    } catch (e) {
      toast.error(erroDe(e, 'A Meta não aceitou o modelo.'));
    } finally {
      setCriandoModelo(false);
    }
  };

  const enviarTeste = async (e) => {
    e.preventDefault();
    setTestando(true);
    try {
      const r = await axios.post(`${apiUrl}/meta/teste`, { sanitizedClientId: tenantId, telefone: telefoneTeste }, { headers: await cabecalhos() });
      toast.success(r.data.aviso || `Aviso enviado (modelo ${r.data.enviado}). Confira no WhatsApp.`);
    } catch (erro) {
      toast.error(erroDe(erro, 'Não foi possível enviar o teste.'));
    } finally {
      setTestando(false);
    }
  };

  if (erroCarga || !form) {
    return (
      <section className="qz-card cw" aria-labelledby="cw-titulo">
        <h2 id="cw-titulo">Conexão com o WhatsApp</h2>
        <p className={erroCarga ? 'cw-erro' : 'cw-suave'}>{erroCarga || 'Carregando…'}</p>
      </section>
    );
  }

  const meta = form.provedor === 'meta';
  const metaSalva = salvo.provedor === 'meta';
  const statusModelo = modelo.dados && (STATUS_MODELO[modelo.dados.status] || { texto: modelo.dados.status, classe: '' });

  return (
    <section className="qz-card cw" aria-labelledby="cw-titulo">
      <h2 id="cw-titulo">Conexão com o WhatsApp</h2>

      {salvo.webDisponivel !== false && (
        <div className="cw-opcoes" role="radiogroup" aria-label="Tipo de conexão">
          <label className={`cw-opcao${meta ? '' : ' cw-opcao--ativa'}`}>
            <input type="radio" name="cw-provedor" checked={!meta} onChange={() => mudar('provedor', 'web')} />
            <span>
              <strong>WhatsApp Web (QR Code)</strong>
              <span className="cw-suave">Liga um celular lendo o QR Code. Bom para testar, mas não é oficial: o número pode ser bloqueado pelo WhatsApp.</span>
            </span>
          </label>
          <label className={`cw-opcao${meta ? ' cw-opcao--ativa' : ''}`}>
            <input type="radio" name="cw-provedor" checked={meta} onChange={() => mudar('provedor', 'meta')} />
            <span>
              <strong>API oficial da Meta</strong>
              <span className="cw-suave">WhatsApp Business Platform: número da empresa, sem celular ligado. A Meta cobra pelas mensagens de modelo.</span>
            </span>
          </label>
        </div>
      )}

      {meta && (
        <>
          {metaSalva && salvo.ultimoErro && (
            <div className="cw-alerta" role="alert">
              <FiAlertTriangle aria-hidden="true" />
              <div>
                <strong>Último problema com a Meta</strong>
                <span>{salvo.ultimoErro.texto}</span>
                <span className="cw-suave">{new Date(salvo.ultimoErro.em).toLocaleString('pt-BR')}</span>
              </div>
            </div>
          )}

          <div className="cw-bloco" role="group" aria-labelledby="cw-dados">
            <h3 id="cw-dados">Dados do app na Meta</h3>
            <p className="cw-suave">Em Meta for Developers › seu app › WhatsApp › Configuração da API.</p>
            <div className="cw-campos">
              <Campo id="cw-phone" rotulo="ID do número de telefone" valor={form.phoneNumberId} onChange={(v) => mudar('phoneNumberId', v)} />
              <Campo id="cw-waba" rotulo="ID da conta do WhatsApp Business (WABA)" valor={form.wabaId} onChange={(v) => mudar('wabaId', v)} />
              <Campo
                id="cw-token"
                tipo="password"
                rotulo="Token de acesso permanente"
                valor={form.accessToken}
                onChange={(v) => mudar('accessToken', v)}
                placeholder={salvo.temToken ? 'Salvo. Preencha só para trocar' : ''}
                ajuda="De um usuário do sistema no Gerenciador de Negócios, com as permissões whatsapp_business_messaging e whatsapp_business_management."
              />
              <Campo
                id="cw-secret"
                tipo="password"
                rotulo="App Secret"
                valor={form.appSecret}
                onChange={(v) => mudar('appSecret', v)}
                placeholder={salvo.temAppSecret ? 'Salvo. Preencha só para trocar' : ''}
                ajuda="Configurações do app › Básico › Chave secreta do aplicativo. Confere que as mensagens vêm mesmo da Meta."
              />
            </div>
            {metaSalva && salvo.numeroExibido && (
              <p className="cw-ok">
                <FiCheck aria-hidden="true" /> Número conferido na Meta: <strong>{salvo.numeroExibido}</strong>
                {salvo.nomeVerificado ? ` · ${salvo.nomeVerificado}` : ''}
              </p>
            )}
          </div>

          <div className="cw-bloco" role="group" aria-labelledby="cw-webhook">
            <h3 id="cw-webhook">Webhook</h3>
            {salvo.verifyToken ? (
              <>
                <p className="cw-suave">
                  Em WhatsApp › Configuração › Webhook, cole os dois valores e assine o campo <code>messages</code>.
                  Se o número também é usado no app WhatsApp Business, assine <code>smb_message_echoes</code>: assim o
                  bot pausa quando alguém da equipe responde pelo celular.
                </p>
                <Copiar rotulo="URL de retorno" valor={`${apiUrl}/webhook/meta/${tenantId}`} />
                <Copiar rotulo="Token de verificação" valor={salvo.verifyToken} />
              </>
            ) : (
              <p className="cw-suave">Salve os dados acima para gerar o endereço e o token de verificação do webhook.</p>
            )}
          </div>

          <div className="cw-bloco" role="group" aria-labelledby="cw-modelo">
            <h3 id="cw-modelo">Modelo de aviso (janela de 24h)</h3>
            <p className="cw-suave">
              A Meta só deixa o bot escrever livremente até 24h depois da última mensagem do aluno. Fora disso (aula
              agendada, lembrete, matrícula pelo painel), o aluno recebe este aviso e o conteúdo chega quando ele toca
              em <strong>Receber</strong>.
            </p>
            <div className="cw-modelo">
              <div className="cw-balao" aria-label="Prévia do aviso no WhatsApp">
                <p>{comNegrito(salvo.textoModelo)}</p>
                <span className="cw-balao__botao">{salvo.botaoModelo}</span>
              </div>
              <div className="cw-modelo__lado">
                <div className="cw-campos cw-campos--2">
                  <Campo id="cw-modelo-nome" rotulo="Nome do modelo" valor={form.modeloNome} onChange={(v) => mudar('modeloNome', v)} />
                  <Campo id="cw-modelo-idioma" rotulo="Idioma" valor={form.modeloIdioma} onChange={(v) => mudar('modeloIdioma', v)} />
                </div>
                <div className="cw-modelo__status" aria-live="polite">
                  {!metaSalva || !salvo.wabaId ? (
                    <span className="cw-suave">Salve a conexão com o ID da conta (WABA) para criar e acompanhar o modelo.</span>
                  ) : modelo.carregando ? (
                    <span className="cw-suave">Consultando a Meta…</span>
                  ) : modelo.erro ? (
                    <span className="cw-erro">{modelo.erro}</span>
                  ) : modelo.dados ? (
                    <>
                      <span className={`qz-badge ${statusModelo.classe}`}>{statusModelo.texto}</span>
                      {modelo.dados.rejected_reason && modelo.dados.rejected_reason !== 'NONE' && (
                        <span className="cw-suave">Motivo: {modelo.dados.rejected_reason}</span>
                      )}
                    </>
                  ) : modelo.dados === null ? (
                    <>
                      <span className="qz-badge">Ainda não criado</span>
                      <button type="button" className="btn-primary cw-botao" onClick={criarModelo} disabled={criandoModelo}>
                        {criandoModelo ? 'Enviando…' : 'Criar modelo na Meta'}
                      </button>
                    </>
                  ) : null}
                  {metaSalva && salvo.wabaId && !modelo.carregando && (
                    <button type="button" className="btn-ghost cw-botao" onClick={consultarModelo}>
                      <FiRefreshCw aria-hidden="true" /> Atualizar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {metaSalva && (
            <div className="cw-bloco" role="group" aria-labelledby="cw-teste">
              <h3 id="cw-teste">Enviar teste</h3>
              <form className="cw-teste" onSubmit={enviarTeste}>
                <label htmlFor="cw-teste-tel" className="cw-oculto">Número para o teste</label>
                <input
                  id="cw-teste-tel"
                  type="tel"
                  inputMode="tel"
                  value={telefoneTeste}
                  onChange={(e) => setTelefoneTeste(e.target.value)}
                  placeholder="55 11 99999-8888"
                />
                <button type="submit" className="btn-secondary cw-botao" disabled={testando || !telefoneTeste.replace(/\D/g, '')}>
                  <FiSend aria-hidden="true" /> {testando ? 'Enviando…' : 'Enviar aviso'}
                </button>
              </form>
              <p className="cw-suave">Com o número de teste da Meta, só recebem os números cadastrados como destinatários no app.</p>
            </div>
          )}
        </>
      )}

      <div className="cw-acoes">
        <span className="cw-suave">
          {alterado ? 'Há alterações não salvas.' : metaSalva ? 'Usando a API oficial da Meta.' : 'Usando WhatsApp Web.'}
        </span>
        <button type="button" className="btn-primary cw-botao" onClick={salvar} disabled={!alterado || salvando}>
          {salvando ? (meta ? 'Conferindo na Meta…' : 'Salvando…') : 'Salvar conexão'}
        </button>
      </div>
    </section>
  );
}

export default ConexaoWhatsApp;
